import { roundFCFA } from '@shared/utils/round';
import {
  JOURS_MOIS_PAIE,
  PLAFOND_TRANSPORT_EXONERE,
  PLAFOND_VEHICULE_EXONERE,
} from '@shared/constants/Taux';
import { CategorieRubrique, type AgregatsRubriques, type RubriquePaie } from './CategorieRubrique';
import { splitIndemniteExoneration } from './IndemnitePlafond';
import type { SaisieBulletin } from './SaisieBulletin';

export interface RubriquesDepuisSaisieOptions {
  /** Jours payés (trentièmes) — proratise le plafond d’exonération. */
  joursPayes?: number;
  /** Toute l’indemnité transport entre en cat2 (aucune part exonérée). */
  ignorerExonerations?: boolean;
}

/**
 * Codes de rubriques du référentiel paie v2 (onglet RUBRIQUES) — source unique des codes.
 * Les rubriques du moteur absentes du référentiel portent un code interne `X-…` :
 * à faire coder par l'expert-paie (voir `docs/paie/parametres-paie.md`).
 */
export const CODES_RUBRIQUES = {
  SALAIRE_BASE: '10000',
  SURSALAIRE: '10150',
  RAPPEL_CONTRAT: '10410',
  PRIME_ECHANTILLONS: '11720',
  PRIME_VEHICULE_IMPOSABLE: '11940',
  PRIME_LOGEMENT_IMPOSABLE: '11950',
  INDEMNITE_LOGEMENT: '15000',
  PRIME_TRANSPORT_CNSS: '15050',
  INDEMNITE_VEHICULE_CNSS: '15100',
  INDEMNITE_RESPONSABILITE: '15270',
  INDEMNITE_REPRESENTATION: '15320',
  CNSS_PRECOMPTE: '25150',
  CNAMGS_PRECOMPTE: '25160',
  FNH_BASE: '25200',
  CFP_BASE: '25250',
  TCS_PRECOMPTE: '31500',
  IRPP_PRECOMPTE: '32500',
  INDEMNITE_TRANSPORT: '34050',
  INDEMNITE_VEHICULE: '34080',
  INDEMNITE_BLANCHISSAGE: '34150',
  AVANCES_SALAIRE: '40050',
  // Hors référentiel (codes internes, à faire coder par l'expert-paie)
  PRIME_EXTRA: 'X-PRIME-EXTRA',
  PRIME_ANCIENNETE: 'X-ANCIENNETE',
  TREIZIEME_MOIS: 'X-13EME-MOIS',
  HEURES_SUP: 'X-HEURES-SUP',
  AUTRES_IMPOSABLES: 'X-AUTRES',
  AVANTAGE_NATURE: 'X-AVANTAGE-NATURE',
} as const;

/**
 * Construit la liste de rubriques classifiées à partir de la saisie plate
 * (rétrocompat) + rubriques libres optionnelles. C'est cette liste qui ALIMENTE LE CALCUL
 * (`calculerPaieRubriques`) : la catégorie décide des bases.
 *
 * Mapping bulletins de référence (référentiel paie v2) :
 * - Cat1 « complète » (CNSS + CNAMGS + TCS + IRPP) : salaire, sursalaire, primes, HS, autres, AEN
 * - Cat2 « CNSS seule » (dans le brut, base CNSS, hors CNAMGS et TCS) : logement 15000,
 *   transport 15050, véhicule 15100, responsabilité 15270, représentation 15320
 * - Cat3 non cotisable (hors brut, ajoutée au net) : 34050, 34080, 34150
 */
export function rubriquesDepuisSaisie(
  saisie: SaisieBulletin,
  options: RubriquesDepuisSaisieOptions = {},
): RubriquePaie[] {
  const push = (
    list: RubriquePaie[],
    code: string,
    libelle: string,
    categorie: CategorieRubrique,
    montant: number | undefined,
    exoIrppGroupe?: 'art91_11',
  ): void => {
    const m = roundFCFA(montant ?? 0);
    if (m === 0) return;
    list.push({
      code,
      libelle,
      categorie,
      montant: m,
      ...(exoIrppGroupe ? { exoIrppGroupe } : {}),
    });
  };

  const out: RubriquePaie[] = [];
  const C = CODES_RUBRIQUES;

  push(
    out,
    C.SALAIRE_BASE,
    'Salaire de base',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.salaire_base,
  );
  push(out, C.SURSALAIRE, 'Sursalaire', CategorieRubrique.CAT1_IMPOSABLE, saisie.sursalaire);
  // « Prime » (11720, prime échantillons) : imposable à l'IRPP et à la TCS sur les bulletins réels de 2026
  // (référentiel paie v2, plus récent que le CGI 2025) : PAS dans le groupe art. 91 11°.
  push(out, C.PRIME_ECHANTILLONS, 'Prime', CategorieRubrique.CAT1_IMPOSABLE, saisie.prime);
  push(
    out,
    C.PRIME_EXTRA,
    'Prime extra',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.prime_extra,
    'art91_11',
  );
  push(
    out,
    C.PRIME_LOGEMENT_IMPOSABLE,
    'Prime de logement imposable',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.prime_logement,
  );
  push(
    out,
    C.PRIME_VEHICULE_IMPOSABLE,
    'Prime véhicule imposable',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.prime_vehicule,
  );
  push(
    out,
    C.PRIME_ANCIENNETE,
    'Prime ancienneté',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.prime_anciennete,
  );
  push(
    out,
    C.TREIZIEME_MOIS,
    '13e mois',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.treizieme_mois,
    'art91_11',
  );
  push(
    out,
    C.HEURES_SUP,
    'Heures supplémentaires',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.hsup_total,
  );
  push(
    out,
    C.AUTRES_IMPOSABLES,
    'Autres éléments imposables',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.autres,
  );
  push(
    out,
    C.AVANTAGE_NATURE,
    'Avantages en nature',
    CategorieRubrique.CAT1_IMPOSABLE,
    saisie.avantage_nature,
  );

  push(
    out,
    C.INDEMNITE_LOGEMENT,
    'Indemnité logement',
    CategorieRubrique.CAT2_INDEM_BRUT,
    saisie.logement,
  );
  push(
    out,
    C.INDEMNITE_RESPONSABILITE,
    'Indemnité de responsabilité',
    CategorieRubrique.CAT2_INDEM_BRUT,
    saisie.fonction_ind,
  );
  push(
    out,
    C.INDEMNITE_REPRESENTATION,
    'Indemnité de représentation',
    CategorieRubrique.CAT2_INDEM_BRUT,
    saisie.ind_representation,
  );

  const jours = saisie.jours_travailles ?? options.joursPayes ?? JOURS_MOIS_PAIE;
  pushIndemnitePlafonnee(push, out, {
    verse: saisie.transport,
    jours,
    ignorerExonerations: options.ignorerExonerations === true,
    plafond: PLAFOND_TRANSPORT_EXONERE,
    codeCotisable: C.PRIME_TRANSPORT_CNSS,
    libelleCotisable: 'Prime de Transport CNSS',
    codeExonere: C.INDEMNITE_TRANSPORT,
    libelleExonere: 'Indemnité de Transport',
  });

  const vehiculeTotal = roundFCFA(saisie.vehicule ?? 0);
  if (vehiculeTotal > 0) {
    pushIndemnitePlafonnee(push, out, {
      verse: vehiculeTotal,
      jours,
      ignorerExonerations: options.ignorerExonerations === true,
      plafond: PLAFOND_VEHICULE_EXONERE,
      codeCotisable: C.INDEMNITE_VEHICULE_CNSS,
      libelleCotisable: 'Indemnité Véhicule CNSS',
      codeExonere: C.INDEMNITE_VEHICULE,
      libelleExonere: 'Indemnité de Véhicule',
    });
  } else {
    push(
      out,
      C.INDEMNITE_VEHICULE_CNSS,
      'Indemnité Véhicule CNSS',
      CategorieRubrique.CAT2_INDEM_BRUT,
      saisie.ind_vehicule_cnss,
    );
    push(
      out,
      C.INDEMNITE_VEHICULE,
      'Indemnité de Véhicule',
      CategorieRubrique.CAT3_NON_COTISAL,
      saisie.ind_vehicule_nc,
    );
  }

  push(
    out,
    C.INDEMNITE_BLANCHISSAGE,
    'Indemnité blanchissage',
    CategorieRubrique.CAT3_NON_COTISAL,
    saisie.ind_blanchissage,
  );

  if (Array.isArray(saisie.rubriques)) {
    for (const r of saisie.rubriques) {
      const m = roundFCFA(r.montant ?? 0);
      if (m === 0) continue;
      out.push({
        code: r.code,
        libelle: r.libelle,
        categorie: r.categorie,
        montant: m,
      });
    }
  }

  return out;
}

export function agregerRubriques(rubriques: readonly RubriquePaie[]): AgregatsRubriques {
  let cat1_total = 0;
  let cat2_total = 0;
  let cat3_total = 0;
  for (const r of rubriques) {
    const m = roundFCFA(r.montant);
    if (r.categorie === CategorieRubrique.CAT1_IMPOSABLE) cat1_total += m;
    else if (r.categorie === CategorieRubrique.CAT2_INDEM_BRUT) cat2_total += m;
    else if (r.categorie === CategorieRubrique.CAT3_NON_COTISAL) cat3_total += m;
  }
  return {
    cat1_total: roundFCFA(cat1_total),
    cat2_total: roundFCFA(cat2_total),
    cat3_total: roundFCFA(cat3_total),
    brut_total: roundFCFA(cat1_total + cat2_total),
  };
}

type PushRubrique = (
  list: RubriquePaie[],
  code: string,
  libelle: string,
  categorie: CategorieRubrique,
  montant: number | undefined,
) => void;

function pushIndemnitePlafonnee(
  push: PushRubrique,
  out: RubriquePaie[],
  opts: {
    verse: number | undefined;
    jours: number;
    ignorerExonerations: boolean;
    plafond: number;
    codeCotisable: string;
    libelleCotisable: string;
    codeExonere: string;
    libelleExonere: string;
  },
): void {
  const verse = roundFCFA(opts.verse ?? 0);
  if (verse <= 0) return;
  if (opts.ignorerExonerations) {
    push(out, opts.codeCotisable, opts.libelleCotisable, CategorieRubrique.CAT2_INDEM_BRUT, verse);
    return;
  }
  const split = splitIndemniteExoneration(verse, opts.plafond, opts.jours);
  push(
    out,
    opts.codeCotisable,
    opts.libelleCotisable,
    CategorieRubrique.CAT2_INDEM_BRUT,
    split.cotisable,
  );
  push(
    out,
    opts.codeExonere,
    opts.libelleExonere,
    CategorieRubrique.CAT3_NON_COTISAL,
    split.exonere,
  );
}
